// @flow
import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { useEstimateRateCardId } from '@src/custom-hooks/useEstimateRateCardId';
import { useIsUserAdmin } from '@src/custom-hooks/useIsUserAdmin';
import { useEstimateMLDForFieldGuidance } from '@src/custom-hooks/useEstimateMLDForFieldGuidance';

import {
  DEFAULT_VALUE_FOR_DB_ROW_ID,
  IS_ROW_FROZEN,
  IS_ROW_OPENED_FOR_EDITING,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_BACKEND_NAMES,
  RATE_CARD_ID_BACKEND_NAME,
  ROW_ID,
} from '../../../constants';
import { AddButton } from '../../presentational/buttons/AddButton';
import { FieldArrayGridConfigItemContext } from '../../../formik-utils/FieldArrayGrid';
import {
  getDimensionsColumnConfig,
  getIfAllMetricsAreFrozen,
  getIfAnyMetricIsBeingEdited,
} from '../utils';
import { getIfMetricHasGuidanceAndUsage } from '../mld-for-field/utils';

const getIfMetricNameAddButtonShouldBeDisabled = (
  readOnlyMode,
  rowValues,
  formikValues,
  isUserAdmin,
  mldGuidance
) => {
  if (readOnlyMode) {
    return true;
  }

  if (rowValues?.metricName == null) {
    return true;
  }

  // Non-admin users cannot add dimensions for metrics not present in guidance
  if (!isUserAdmin) {
    if (!getIfMetricHasGuidanceAndUsage(rowValues.metricName, mldGuidance)) {
      return true;
    }
  }

  if (rowValues.dimensions_config_array.length === 0) {
    return false;
  }

  if (getIfAnyMetricIsBeingEdited(formikValues)) {
    return true;
  }

  return getIfAllMetricsAreFrozen(formikValues) === false;
};

export const MetricNameAddButtonContainer = ({ metricDimensionRefs }) => {
  const formik = useFormikContext();
  const { rowValues, readOnlyMode } = useContext(FieldArrayGridConfigItemContext);
  const rateCardId = useEstimateRateCardId();
  const isUserAdmin = useIsUserAdmin();
  const mldGuidance = useEstimateMLDForFieldGuidance();
  const disabled = getIfMetricNameAddButtonShouldBeDisabled(
    readOnlyMode,
    rowValues,
    formik.values,
    isUserAdmin,
    mldGuidance
  );

  return (
    <AddButton
      disabled={disabled}
      onClick={() => {
        const newRow = {
          [IS_ROW_FROZEN]: false,
          [IS_ROW_OPENED_FOR_EDITING]: true,
          [ROW_ID]: DEFAULT_VALUE_FOR_DB_ROW_ID,
          [RATE_CARD_ID_BACKEND_NAME]: rateCardId,
        };

        const dimensionColumnsConfiguration =
          metricDimensionRefs.current.dimensionColumnsConfiguration;

        // Dependent dimension cols BE Names
        newRow[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_BACKEND_NAMES] =
          getDimensionsColumnConfig(dimensionColumnsConfiguration).map((x) => x.backendFieldName);

        // Fill default dependent dimension values
        for (const colConfig of dimensionColumnsConfiguration) {
          newRow[colConfig.backendFieldName] = colConfig.colDefaultValue;
        }

        return metricDimensionRefs.current.dimensionArrayHelpers.push(newRow);
      }}
    />
  );
};
